import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { Logo } from "@/components/Logo";
import { LangSwitcher } from "@/components/LangSwitcher";
import { useState } from "react";
import { List, X } from "@phosphor-icons/react";

const PUBLIC_LINKS = [
  { to: "/", key: "home", route: "inicio" },
  { to: "/planes", key: "plans", route: "planes" },
  { to: "/historias", key: "stories", route: "historias" },
  { to: "/evaluacion", key: "evaluation", route: "evaluacion" },
  { to: "/biblioteca", key: "library", route: "biblioteca" },
];

const APP_LINKS = [
  { to: "/dashboard", key: "dashboard", route: "panel" },
  { to: "/nutricion", key: "nutrition", route: "nutricion" },
  { to: "/entrenamiento", key: "training", route: "rutinas" },
  { to: "/seguimiento", key: "tracking", route: "seguimiento" },
  { to: "/biblioteca", key: "library", route: "biblioteca" },
];

const ADMIN_LINKS = [
  { to: "/whatsapp", key: "whatsapp", route: "whatsapp" },
];

export const Header = () => {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const links = user ? APP_LINKS : PUBLIC_LINKS;

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 bg-as-black/95 backdrop-blur-xl border-b border-as-border"
      data-testid="header"
      role="banner"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to={user ? "/dashboard" : "/"} className="flex items-center" data-testid="header-logo-link" aria-label="AS TRAINING">
          <Logo size="sm" />
        </Link>
        <nav
          className="hidden md:flex items-center gap-7"
          role="navigation"
          aria-label={t("nav.aria_main_nav")}
        >
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.route}`}
              className={`as-label hover:text-as-green transition-colors ${
                location.pathname === l.to ? "text-as-green" : "text-white"
              }`}
            >
              {t(`nav.${l.key}`)}
            </Link>
          ))}
          {user && user.role === "admin" && (
            <>
              {ADMIN_LINKS.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-${l.route}`}
                  className={`as-label hover:text-as-green transition-colors ${
                    location.pathname === l.to ? "text-as-green" : "text-white"
                  }`}
                >
                  {t(`nav.${l.key}`)}
                </Link>
              ))}
              <Link to="/admin" data-testid="nav-admin" className="as-label text-as-green">
                {t("nav.admin")}
              </Link>
            </>
          )}
        </nav>
        <div className="hidden md:flex items-center gap-3">
          <LangSwitcher variant="header" />
          {user ? (
            <>
              <span className="as-label text-white">{user.name?.split(" ")[0]}</span>
              <button onClick={logout} className="as-btn-secondary !py-2 !px-4 !text-xs" data-testid="logout-btn">
                {t("nav.logout")}
              </button>
            </>
          ) : (
            <button
              onClick={() => navigate("/login")}
              className="as-btn-primary !py-2 !px-5 !text-xs"
              data-testid="header-login-btn"
            >
              {t("nav.login")}
            </button>
          )}
        </div>
        <button
          onClick={() => setOpen(!open)}
          className="md:hidden text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-as-green rounded-sm"
          data-testid="mobile-menu-toggle"
          aria-label={open ? t("nav.menu_close") : t("nav.menu_open")}
          aria-expanded={open}
          aria-controls="mobile-nav-menu"
        >
          {open ? <X size={28} aria-hidden="true" /> : <List size={28} aria-hidden="true" />}
        </button>
      </div>
      {open && (
        <div
          id="mobile-nav-menu"
          className="md:hidden border-t border-as-border bg-as-black"
          role="navigation"
          aria-label={t("nav.aria_mobile_nav")}
        >
          <div className="px-4 py-4 flex flex-col gap-3">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                className="as-label text-white py-2"
                data-testid={`mobile-nav-${l.route}`}
              >
                {t(`nav.${l.key}`)}
              </Link>
            ))}
            {user && user.role === "admin" && (
              <>
                {ADMIN_LINKS.map((l) => (
                  <Link
                    key={l.to}
                    to={l.to}
                    onClick={() => setOpen(false)}
                    className="as-label text-white py-2"
                    data-testid={`mobile-nav-${l.route}`}
                  >
                    {t(`nav.${l.key}`)}
                  </Link>
                ))}
                <Link to="/admin" onClick={() => setOpen(false)} className="as-label text-as-green py-2">
                  {t("nav.admin")}
                </Link>
              </>
            )}
            <div className="pt-2 border-t border-as-border mt-2">
              <LangSwitcher variant="mobile" />
            </div>
            {user ? (
              <button onClick={logout} className="as-btn-secondary mt-2" data-testid="mobile-logout">
                {t("nav.logout")}
              </button>
            ) : (
              <button
                onClick={() => { setOpen(false); navigate("/login"); }}
                className="as-btn-primary mt-2"
                data-testid="mobile-login-btn"
              >
                {t("nav.login")}
              </button>
            )}
          </div>
        </div>
      )}
    </header>
  );
};
